import { z } from "zod";

import { hmiExpressionRuleSchema } from "./hmi-expression.schema";

export const hmiTimeRangeSchema = z.enum(["last_5m", "last_1h", "last_24h", "last_7d"]);
export const hmiAggregationSchema = z.enum(["avg", "min", "max"]);
export const hmiReadModeSchema = z.enum(["live", "window"]);

export const hmiTelemetrySourceSchema = z
  .object({
    kind: z.literal("telemetry"),
    deviceId: z.string().uuid(),
    channel: z.string().trim().min(1).max(128).nullable().optional(),
    formula: z.string().trim().min(1).max(4000).nullable().optional(),
    metricId: z.string().uuid().nullable().optional(),
    readMode: hmiReadModeSchema.default("live"),
    timeRange: hmiTimeRangeSchema,
    aggregation: hmiAggregationSchema,
  })
  .superRefine((value, ctx) => {
    const hasChannel = Boolean(value.channel);
    const hasFormula = Boolean(value.formula);
    const hasMetric = Boolean(value.metricId);
    if ([hasChannel, hasFormula, hasMetric].filter(Boolean).length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Exactly one of channel, formula, or metricId is required.",
      });
    }
  });

export const hmiStaticValueSourceSchema = z.object({
  kind: z.literal("static"),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]).default(null),
});

export const hmiBindingSourceSchema = z.union([hmiTelemetrySourceSchema, hmiStaticValueSourceSchema]);

export const hmiBindingTargetSchema = z.enum([
  "value",
  "text",
  "color",
  "stroke",
  "visible",
  "opacity",
  "rotation",
  "status",
  "chart_series",
  "gauge_value",
]);

export const hmiBindingSchema = z.object({
  id: z.string().trim().min(1).max(128),
  elementId: z.string().trim().min(1).max(128),
  target: hmiBindingTargetSchema,
  label: z.string().trim().min(1).max(128).nullable().optional(),
  source: hmiBindingSourceSchema,
  rules: z.array(hmiExpressionRuleSchema).max(16).default([]),
  format: z
    .object({
      decimals: z.number().int().min(0).max(6).nullable().optional(),
      prefix: z.string().max(32).nullable().optional(),
      suffix: z.string().max(32).nullable().optional(),
      nullText: z.string().max(128).nullable().optional(),
    })
    .nullable()
    .optional(),
});

export type HmiBinding = z.infer<typeof hmiBindingSchema>;
export type HmiBindingSource = z.infer<typeof hmiBindingSourceSchema>;
